import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, Search, RefreshCw, Clock, User, FileText, Activity, Filter, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function AuditLog() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    entity_type: '',
    patient_id: '',
    encounter_id: '',
    limit: 100,
  });

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.patient_id) params.append('patient_id', filters.patient_id);
      if (filters.encounter_id) params.append('encounter_id', filters.encounter_id);
      params.append('limit', filters.limit.toString());
      const res = await api.get(`/audit?${params.toString()}`);
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const exportCSV = () => {
    if (events.length === 0) return;
    const headers = ['timestamp', 'entity_type', 'entity_id', 'action_type', 'user_id', 'patient_id', 'encounter_id'];
    const rows = events.map(e => headers.map(h => e[h] || '').join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_log_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Audit log exported');
  };

  const actionColor = (action) => {
    if (action?.includes('state_transition')) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (action?.includes('created')) return 'bg-teal-50 text-teal-700 border-teal-200';
    if (action?.includes('updated') || action?.includes('approve')) return 'bg-amber-50 text-amber-700 border-amber-200';
    if (action?.includes('access')) return 'bg-purple-50 text-purple-700 border-purple-200';
    if (action?.includes('ai_stage')) return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  return (
    <div className="animate-fadeIn" data-testid="audit-log-page">
      <div className="p-4 sm:p-6 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <Shield className="w-5 h-5 text-primary" /> Audit Trail
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{total} events recorded</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportCSV} disabled={events.length === 0} data-testid="audit-export-button">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={fetchEvents} data-testid="audit-refresh-button">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="py-3">
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[160px]">
                <Label className="text-xs text-muted-foreground mb-1 block">Entity Type</Label>
                <Select value={filters.entity_type} onValueChange={v => setFilters(f => ({ ...f, entity_type: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="audit-filter-entity-type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="appointment">Appointment</SelectItem>
                    <SelectItem value="encounter">Encounter</SelectItem>
                    <SelectItem value="checkin">Check-in</SelectItem>
                    <SelectItem value="command_center">Command Center</SelectItem>
                    <SelectItem value="chart">Chart Access</SelectItem>
                    <SelectItem value="ai_stage">AI Stage</SelectItem>
                    <SelectItem value="patient">Patient</SelectItem>
                    <SelectItem value="deferred_update">Deferred Update</SelectItem>
                    <SelectItem value="comparison">Comparison</SelectItem>
                    <SelectItem value="clinic_settings">Settings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[160px]">
                <Label className="text-xs text-muted-foreground mb-1 block">Patient ID</Label>
                <Input value={filters.patient_id} onChange={e => setFilters(f => ({ ...f, patient_id: e.target.value }))}
                  placeholder="Filter by patient..." className="h-8 text-xs" data-testid="audit-filter-patient" />
              </div>
              <div className="flex-1 min-w-[160px]">
                <Label className="text-xs text-muted-foreground mb-1 block">Encounter ID</Label>
                <Input value={filters.encounter_id} onChange={e => setFilters(f => ({ ...f, encounter_id: e.target.value }))}
                  placeholder="Filter by encounter..." className="h-8 text-xs" data-testid="audit-filter-encounter" />
              </div>
              <Button size="sm" className="h-8" onClick={fetchEvents} data-testid="audit-apply-filters">
                <Filter className="w-3 h-3 mr-1" /> Apply
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Events List */}
        <Card>
          <ScrollArea className="h-[calc(100vh-320px)]">
            <CardContent className="pt-4">
              {loading ? (
                <div className="space-y-3">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="flex gap-3 items-start">
                      <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">No audit events match the current filters</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {events.map((event, i) => (
                    <div key={event.id || i} className="flex gap-3 items-start py-2 border-b last:border-0" data-testid="audit-event-row">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                        {event.action_type?.includes('state_transition') ? <Activity className="w-4 h-4 text-blue-500" /> :
                         event.action_type?.includes('access') ? <FileText className="w-4 h-4 text-purple-500" /> :
                         event.action_type?.includes('ai_stage') ? <Activity className="w-4 h-4 text-indigo-500" /> :
                         <Shield className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] ${actionColor(event.action_type)}`}>
                            {event.action_type}
                          </Badge>
                          <Badge variant="outline" className="text-[10px]">{event.entity_type}</Badge>
                          {event.previous_value && event.new_value && typeof event.previous_value === 'string' && typeof event.new_value === 'string' && (
                            <span className="text-[10px] text-muted-foreground">
                              {event.previous_value} → {event.new_value}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{event.timestamp?.split('.')[0]?.replace('T', ' ')}</span>
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{event.user_id}</span>
                          {event.entity_id && <span className="font-mono truncate max-w-[200px]">{event.entity_id}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
}
